// biome-ignore-all lint/suspicious/noArrayIndexKey: flagged lists are fee items, action-sheet items, session-date buttons, and custom uniform items — all derived directly from request data on each render with a stable order.
// biome-ignore-all lint/a11y/useSemanticElements: the child selection card uses div+role="button" intentionally — it wraps category buttons (interactive children), making a real <button> wrapper invalid nested-button HTML. It has role + tabIndex + onKeyDown for full keyboard a11y.
// biome-ignore-all lint/security/noDangerouslySetInnerHtml: the action-sheet body renders admin-authored next-steps templates through sanitizeRichText (whitelist: b/u/span[font-size]/br), never raw user input.
//
// =============================================================================
// ShelterRequestView.jsx
// =============================================================================
// Read-only shelter-side view of a submitted (non-draft) request.
// Extracted from RequestDetail.jsx (Phase D, May 2026).
//
// Shows a 3-column info strip (General Info | Latest Update | Request Summary),
// per-child category cards with session-date action-sheet popups, and a child
// detail panel when a card is selected. The outer guard condition
// (!isStaff && !draft) lives in ReqDetail; this component renders
// unconditionally once mounted.
//
// Props
// ─────
//   req                — the request object
//   notes              — the notes state array (from ReqDetail)
//   shelterSel         — { ci, cat } | null — which child+category is selected
//   setShelterSel      — setter for shelterSel
//   actionSheetOpen    — { ci, sessionDate } | null — open action sheet
//   setActionSheetOpen — setter for actionSheetOpen
//   setShowCommentPanel — opens the comment panel slide-in
//   feeTotal / grand / childUniTotal / childShoeTotal / childLapTotal
//                      — pre-computed request totals passed from ReqDetail
//   nextStepsTemplates — loaded from DB; used for action-sheet next-steps text
// =============================================================================

import { supplierMeta, useUniformSuppliers } from "../../hooks/useUniformSuppliers";
import { collectSessionDates } from "../../services/actionSheets";
import { sectionButtonBackground } from "../../services/approvals";
import { plainTextDisplayHtml } from "../../services/richText";
import { C, PANEL } from "../../utils/constants";
import { APPROVAL_STATUSES } from "../../utils/enums";
import { calcUniItems } from "../../utils/formConstants";
import { GLYPH } from "../../utils/glyphs";
import { money } from "../../utils/helpers";
import { formatPhone } from "../../utils/sanitize";
import { openSchoolLockerOrderPdf } from "../../utils/schoolLockerOrderPdf";
import { openUniformAuthorityPdf } from "../../utils/uniformAuthorityPdf";
import { HelpButton } from "../form/HelpButton.jsx";
import { printChild } from "../form/PrintButton.jsx";
import { ActionSheetButtons } from "./ActionSheetButtons.jsx";
import { ActionSheetModal } from "./ActionSheetModal.jsx";
import { ChildItemReadView } from "./ChildItemReadView.jsx";

// itemOutcomeMessage moved to services/actionSheets (shared with the responder
// view); re-exported here so existing imports of this path keep working.
export { itemOutcomeMessage } from "../../services/actionSheets";

export function ShelterRequestView({
  req,
  notes,
  // THE FENCE (John, 27 Aug 2026): the shelter "Last Comment" conversation panel
  // renders ONLY for an actual shelter caseworker. Defaults true (historical
  // behaviour); RequestDetail passes false for a Zephyr viewer (e.g. a picker browsing
  // All Requests), who reads the request read-only here but takes comments through the
  // internal Comments button in the title bar — never the shelter conversation surface.
  isShelterUser = true,
  shelterSel,
  setShelterSel,
  actionSheetOpen,
  setActionSheetOpen,
  setShowCommentPanel,
  hasUnread,
  onCommentsOpened,
  feeTotal,
  grand,
  childUniTotal,
  childShoeTotal,
  childLapTotal,
  nextStepsTemplates,
  user,
}) {
  // Admin-driven supplier metadata (db-46) — drives which print buttons appear.
  const { suppliers: uniformSuppliers } = useUniformSuppliers();
  return (
    <div>
      {/* Top 3-column strip: General Info | Latest Update | Request Summary */}
      {/* ⚠️ CROSS-REFERENCE: the General Information + Last Comment cards below are
          duplicated (deliberately) in src/components/request/TopUpShelterHeader.jsx for
          the shelter-facing Stock Top-up read view. If you change the General-Info
          fields or the "From Zephyr" last-comment logic here, check that file too. */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 12,
          marginBottom: 14,
          alignItems: "stretch",
        }}
      >
        {/* General Information */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            border: `1px solid ${"#CFE0DE"}`,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: C.blue,
              color: "white",
              padding: "11px 18px",
              fontWeight: 700,
              fontSize: 14,
              flexShrink: 0,
            }}
          >
            General Information
          </div>
          <div
            style={{
              padding: "14px 16px",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {[
              ["DFV Service Ref", req.shelterRef],
              ["Contact Name", req.contactName],
              ["Contact Email", req.shelterEmail],
              // Mobile on the LHS, Phone on the RHS (space-between); a lone number
              // sits on the left rather than being floated right by an empty slot.
              [
                "Mobile / Phone",
                req.mobile || req.phone ? (
                  <div
                    key="nums"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      width: "100%",
                      gap: 14,
                    }}
                  >
                    {req.mobile ? <span>{formatPhone(req.mobile)}</span> : null}
                    {req.phone ? <span>{formatPhone(req.phone)}</span> : null}
                  </div>
                ) : null,
              ],
            ].map(function (pair) {
              const l = pair[0];
              const v = pair[1];
              return v ? (
                <div
                  key={l}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: 6,
                    paddingBottom: 6,
                    borderBottom: `1px solid ${"#CFE0DE"}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#607D79",
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      width: 120,
                      flexShrink: 0,
                    }}
                  >
                    {l}
                  </div>
                  <div style={{ fontSize: 13, color: "#1A2E2B", lineHeight: 1.5, flex: 1 }}>
                    {v}
                  </div>
                </div>
              ) : null;
            })}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                marginBottom: 6,
                paddingBottom: 6,
                borderBottom: `1px solid ${"#CFE0DE"}`,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#607D79",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                  width: 120,
                  flexShrink: 0,
                }}
              >
                Delivery
              </div>
              <div style={{ fontSize: 13, color: "#1A2E2B", lineHeight: 1.5 }}>
                {req.deliveryAddress || "—"}
              </div>
            </div>
          </div>
        </div>

        {/* Latest Update from Zephyr — SHELTER-SIDE ONLY (the fence). Hidden for a
            Zephyr viewer; they use the internal Comments button in the title bar. */}
        {isShelterUser && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              border: `1px solid ${"#CFE0DE"}`,
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background: C.blue,
                color: "white",
                padding: "6px 12px 6px 18px",
                fontWeight: 700,
                fontSize: 14,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <span>Last Comment</span>
              {/* The Comments button lives HERE, not in the title bar (John's call,
                5 Jul 2026) — this panel is the conversation surface, so its header
                hosts the affordance. The staff title-bar button is unchanged. */}
              <button
                onClick={function () {
                  if (onCommentsOpened) onCommentsOpened();
                  setShowCommentPanel(true);
                }}
                // db-77: reddy-orange while there's an unread staff comment; reverts
                // to the standard teal the instant the shelter opens Comments.
                style={{
                  background: hasUnread ? "#D84315" : C.blueLight,
                  color: hasUnread ? "white" : C.blue,
                  border: hasUnread ? "1px solid #FFFFFF" : `1px solid ${C.blue}`,
                  borderRadius: 6,
                  padding: "5px 12px",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  whiteSpace: "nowrap",
                }}
              >
                {`${GLYPH.speech} Add/View Comments`}
                {hasUnread ? (
                  // A text chip (not colour alone) so the unread signal stays
                  // accessible to colour-blind users; no animation, so reduced-motion
                  // is moot.
                  <span
                    style={{
                      background: "#FFFFFF",
                      color: "#D84315",
                      borderRadius: 10,
                      padding: "1px 8px",
                      fontSize: 11,
                      fontWeight: 800,
                    }}
                  >
                    New
                  </span>
                ) : notes.length > 0 ? (
                  <span
                    style={{
                      background: "rgba(255,255,255,0.25)",
                      borderRadius: 10,
                      padding: "1px 7px",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    {notes.length}
                  </span>
                ) : null}
              </button>
            </div>
            <div style={{ padding: "14px 16px", flex: 1 }}>
              {(function () {
                // "From Zephyr" = staff-authored notes + approval summaries. Each note
                // carries byRole ("staff"/"shelter") stamped at creation, so this no longer
                // relies on the author name containing "zephyr" (staff comments are authored
                // under a person's name). notes are oldest-first, so the LAST match is newest.
                // (Internal staff notes live in request_internal_notes, never in this array.)
                const zephyrNotes = notes.filter(function (n) {
                  return n.byRole === "staff" || n._summary;
                });
                if (zephyrNotes.length === 0)
                  return (
                    <div style={{ fontSize: 13, color: "#607D79", fontStyle: "italic" }}>
                      No updates yet. You will be notified when Zephyr responds.
                    </div>
                  );
                const latest = zephyrNotes[zephyrNotes.length - 1];
                const maxLen = 300;
                const truncated = latest.text && latest.text.length > maxLen;
                return (
                  <div>
                    <div style={{ fontSize: 11, color: "#607D79", marginBottom: 6 }}>
                      {latest.author} — {latest.date}
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: "#1A2E2B",
                        lineHeight: 1.6,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: plainTextDisplayHtml(
                          truncated ? `${latest.text.slice(0, maxLen)}...` : latest.text
                        ),
                      }}
                    />
                    {truncated && (
                      <button
                        onClick={function () {
                          if (onCommentsOpened) onCommentsOpened();
                          setShowCommentPanel(true);
                        }}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#00897B",
                          fontSize: 16,
                          fontWeight: 600,
                          textDecoration: "underline",
                          cursor: "pointer",
                          fontFamily: "inherit",
                          padding: "4px 0",
                          marginTop: 4,
                        }}
                      >
                        View full comment {GLYPH.arrowRight}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Request Summary */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            border: `1px solid ${"#CFE0DE"}`,
            borderRadius: 10,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              background: C.blue,
              color: "white",
              padding: "11px 18px",
              fontWeight: 700,
              fontSize: 14,
              flexShrink: 0,
            }}
          >
            Request Summary
          </div>
          <div style={{ padding: "14px 16px", flex: 1 }}>
            {feeTotal > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 14,
                  padding: "6px 0",
                  borderBottom: `1px solid ${"#CFE0DE"}`,
                }}
              >
                <span>School Costs & Stationery</span>
                <strong>{money(feeTotal)}</strong>
              </div>
            )}
            {childUniTotal > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 14,
                  padding: "6px 0",
                  borderBottom: `1px solid ${"#CFE0DE"}`,
                }}
              >
                <span>Uniforms</span>
                <strong>{money(childUniTotal)}</strong>
              </div>
            )}
            {childShoeTotal > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 14,
                  padding: "6px 0",
                  borderBottom: `1px solid ${"#CFE0DE"}`,
                }}
              >
                <span>Shoes</span>
                <strong>{money(childShoeTotal)}</strong>
              </div>
            )}
            {childLapTotal > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 14,
                  padding: "6px 0",
                  borderBottom: `1px solid ${"#CFE0DE"}`,
                  color: "#607D79",
                }}
              >
                <span>Device</span>
                <span style={{ fontWeight: 700, color: "#1A2E2B" }}>
                  {money(childLapTotal)} <span style={{ fontSize: 11, fontWeight: 400 }}>est.</span>
                </span>
              </div>
            )}
            {grand > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 17,
                  fontWeight: 700,
                  color: "#00695C",
                  paddingTop: 10,
                  marginTop: 4,
                }}
              >
                <span>TOTAL</span>
                <span>{money(grand)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Child summary cards + detail - same pattern as responder */}
      {/* shelterSel + actionSheetOpen are hooked at the top of ReqDetail (see useState above). */}
      {(function () {
        const CAT_BTNS = [
          { key: "fees", label: "School Costs & Stationery" },
          { key: "uniforms", label: "Uniforms" },
          { key: "shoes", label: "Shoes" },
          { key: "laptop", label: "Device" },
        ];
        const selChild = shelterSel ? (req.children || [])[shelterSel.ci] : null;

        // All confirmed session dates across children (shared helper).
        const allSessionDates = collectSessionDates(req.children);

        return (
          <div>
            {/* Card grid */}
            <div
              style={{
                display: "flex",
                gap: 12,
                overflowX: "auto",
                paddingBottom: 8,
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              {(req.children || []).map(function (child, ci) {
                const hasLaptop = !!child.laptop;
                const isSelected = shelterSel && shelterSel.ci === ci;
                const selectShelterChild = function () {
                  const firstCat = CAT_BTNS.find(function (btn) {
                    return btn.key === "fees"
                      ? child.fees && child.fees.length > 0
                      : btn.key === "uniforms"
                        ? !!child.uniforms
                        : btn.key === "shoes"
                          ? !!child.shoes
                          : !!child.laptop;
                  });
                  if (firstCat) {
                    setShelterSel({ ci: ci, cat: firstCat.key });
                    setActionSheetOpen(null);
                  }
                };
                return (
                  <div
                    key={child._key || ci}
                    role="button"
                    tabIndex={0}
                    onClick={selectShelterChild}
                    onKeyDown={function (e) {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        selectShelterChild();
                      }
                    }}
                    style={{
                      background: isSelected ? "#E0F2F1" : "#FFFFFF",
                      border: `2px solid ${isSelected ? "#00695C" : "#CFE0DE"}`,
                      borderRadius: 12,
                      padding: "12px 14px",
                      minWidth: 180,
                      maxWidth: 220,
                      flexShrink: 0,
                      display: "flex",
                      flexDirection: "column",
                      cursor: "pointer",
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 15,
                        color: "#00695C",
                        marginBottom: 2,
                      }}
                    >
                      {`Child ${ci + 1}`}
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        color: "#607D79",
                        marginBottom: 10,
                        lineHeight: 1.4,
                        minHeight: 40,
                      }}
                    >
                      {(child.gender || "") +
                        (child.yearLevel ? ` · ${child.yearLevel}` : "") +
                        (child.schoolName ? ` · ${child.schoolName}` : "")}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 5,
                        marginBottom: 10,
                        minHeight: 52,
                      }}
                    >
                      {CAT_BTNS.map(function (btn) {
                        const hasData =
                          btn.key === "fees"
                            ? child.fees && child.fees.length > 0
                            : btn.key === "uniforms"
                              ? !!child.uniforms
                              : btn.key === "shoes"
                                ? !!child.shoes
                                : !!child.laptop;
                        if (!hasData) return null;
                        const isSelBtn = isSelected && shelterSel.cat === btn.key;
                        // Determine approval status for this category badge
                        // Rules: Red=all declined, Dark green=all approved, Gold=at least one pending/needs-info, Light green=all decided mixed
                        let catStatuses = [];
                        if (btn.key === "fees") {
                          catStatuses = (child.fees || []).map(function (f) {
                            return f.approvalStatus || "Pending";
                          });
                        } else if (btn.key === "uniforms" && child.uniforms) {
                          catStatuses = [child.uniforms.approvalStatus || "Pending"];
                        } else if (btn.key === "shoes" && child.shoes) {
                          catStatuses = [child.shoes.approvalStatus || "Pending"];
                        } else if (btn.key === "laptop" && child.laptop) {
                          catStatuses = [child.laptop.approvalStatus || "Pending"];
                        }
                        const badgeBg = sectionButtonBackground(catStatuses);
                        return (
                          <button
                            key={btn.key}
                            onClick={function (e) {
                              e.stopPropagation();
                              setShelterSel({ ci: ci, cat: btn.key });
                              setActionSheetOpen(null);
                            }}
                            style={{
                              background: badgeBg,
                              color: "#fff",
                              border: isSelBtn ? "2px solid #1A2E2B" : "2px solid transparent",
                              borderRadius: 6,
                              padding: "4px 9px",
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: "pointer",
                              fontFamily: "inherit",
                              outline: "none",
                              alignSelf: "flex-start",
                            }}
                          >
                            {btn.label}
                          </button>
                        );
                      })}
                    </div>
                    {(function () {
                      let approvedAmt = 0,
                        declinedAmt = 0,
                        unapprovedAmt = 0;
                      (child.fees || []).forEach(function (f) {
                        const amt = parseFloat(f.feeAmount) || 0;
                        if (f.approvalStatus === APPROVAL_STATUSES.APPROVED) approvedAmt += amt;
                        else if (f.approvalStatus === APPROVAL_STATUSES.DECLINED)
                          declinedAmt += amt;
                        else unapprovedAmt += amt;
                      });
                      [
                        {
                          obj: child.uniforms,
                          calc: function (u) {
                            return calcUniItems([u]);
                          },
                        },
                        {
                          obj: child.shoes,
                          calc: function (s) {
                            return (
                              (s.jQty || 0) * (parseFloat(s.jCost) || 0) +
                              (s.lQty || 0) * (parseFloat(s.lCost) || 0)
                            );
                          },
                        },
                        {
                          obj: child.laptop,
                          calc: function (l) {
                            return parseFloat(l.estimatedCost) || 0;
                          },
                        },
                      ].forEach(function (cat) {
                        if (!cat.obj) return;
                        const amt = cat.calc(cat.obj);
                        if (cat.obj.approvalStatus === APPROVAL_STATUSES.APPROVED)
                          approvedAmt += amt;
                        else if (cat.obj.approvalStatus === APPROVAL_STATUSES.DECLINED)
                          declinedAmt += amt;
                        else unapprovedAmt += amt;
                      });
                      // Always show all three lines (John, 8 Aug) — consistent with the
                      // responder view. An all-unapproved child now reads "Unapproved $X
                      // est." instead of a bare total; $0 lines still show (e.g. Declined
                      // $0.00) so the card layout + meaning are identical across children.
                      return (
                        <div
                          style={{
                            fontSize: 14,
                            lineHeight: 1.9,
                            borderTop: `1px solid ${"#CFE0DE"}`,
                            paddingTop: 8,
                            marginTop: "auto",
                          }}
                        >
                          <div style={{ color: "#F9A825", fontWeight: 600 }}>
                            {`Unapproved  ${money(unapprovedAmt)}${hasLaptop ? " est." : ""}`}
                          </div>
                          <div style={{ color: "#C62828", fontWeight: 600 }}>
                            {`Declined      ${money(declinedAmt)}`}
                          </div>
                          <div style={{ color: "#388E3C", fontWeight: 600 }}>
                            {`Approved     ${money(approvedAmt)}`}
                          </div>
                        </div>
                      );
                    })()}
                    {/* Action sheet buttons per session (shared component). */}
                    <ActionSheetButtons
                      child={child}
                      ci={ci}
                      sessionDates={allSessionDates}
                      reqState={req.state || ""}
                      uniformSuppliers={uniformSuppliers}
                      onOpen={setActionSheetOpen}
                    />
                  </div>
                );
              })}
            </div>

            {/* Detail panel */}
            {shelterSel && selChild && (
              <div
                style={{
                  background: "#FFFFFF",
                  border: `1px solid ${"#CFE0DE"}`,
                  borderRadius: 12,
                  padding: "16px 20px",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      background: "#00695C",
                      color: "white",
                      borderRadius: 8,
                      padding: "6px 16px",
                      fontWeight: 700,
                      fontSize: 16,
                    }}
                  >
                    {`Child ${shelterSel.ci + 1}`}
                  </div>
                  {shelterSel.cat === "laptop" && (
                    <HelpButton helpKey="device_specs" label="Device Specs" user={user} />
                  )}
                  {/* Child demographics intentionally omitted here — already shown
                      on the child summary card above. */}
                  <button
                    onClick={function () {
                      setShelterSel(null);
                    }}
                    style={{
                      marginLeft: "auto",
                      background: "none",
                      border: `1px solid ${"#CFE0DE"}`,
                      borderRadius: 6,
                      padding: "4px 12px",
                      fontSize: 12,
                      cursor: "pointer",
                      fontFamily: "inherit",
                      color: "#607D79",
                    }}
                  >
                    Close
                  </button>
                </div>
                {/* Detailed read-only item panel — shared with the responder view
                    (ChildItemReadView). Renders for whichever category is selected. */}
                <ChildItemReadView
                  child={selChild}
                  cat={shelterSel.cat}
                  uniformsActions={(function () {
                    if (
                      shelterSel.cat !== "uniforms" ||
                      !selChild.uniforms ||
                      selChild.uniforms.approvalStatus !== APPROVAL_STATUSES.APPROVED
                    )
                      return null;
                    const meta = supplierMeta(uniformSuppliers, selChild.uniforms.supplier);
                    const isSL = meta.kind === "school_locker";
                    // School Locker shows the electronic order sheet ("Print Order Sent");
                    // suppliers with an authorisation doc show Print Authorisation. Both are
                    // available in the shelter + responder views (John, 7 Jul).
                    if (!isSL && !meta.hasAuthorityDoc) return null;
                    const btnStyle = {
                      background: "none",
                      border: `1px solid ${PANEL.heading}`,
                      color: PANEL.heading,
                      borderRadius: 6,
                      padding: "4px 12px",
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "inherit",
                    };
                    return (
                      <>
                        {isSL && (
                          <button
                            type="button"
                            onClick={async function () {
                              const args = {
                                child: Object.assign({}, selChild.uniforms, {
                                  gender: selChild.gender,
                                  yearLevel: selChild.yearLevel,
                                  schoolName: selChild.schoolName,
                                }),
                                childIndex: shelterSel.ci,
                                shelterName: req.shelterName,
                                refNo: req.refNo,
                                shelterCode: req.shelterCode || "",
                                shelterRef: req.shelterRef || "",
                                reqDate: req.date || "",
                                totalChildren:
                                  (req.children || []).filter(function (ch) {
                                    return (
                                      ch.uniforms &&
                                      ch.uniforms.approvalStatus === APPROVAL_STATUSES.APPROVED
                                    );
                                  }).length || 1,
                              };
                              // Order COPY as a jsPDF (matches the other uniform printouts).
                              // Fall back to the old HTML sheet only if jsPDF failed to load.
                              if (!(await openSchoolLockerOrderPdf(args))) {
                                printChild(Object.assign({ isSL: true }, args));
                              }
                            }}
                            style={btnStyle}
                          >
                            {`${GLYPH.printer} Print Order Sent`}
                          </button>
                        )}
                        {meta.hasAuthorityDoc && (
                          <button
                            type="button"
                            onClick={function () {
                              openUniformAuthorityPdf(req, selChild, shelterSel.ci);
                            }}
                            style={btnStyle}
                          >
                            {`${GLYPH.printer} Print Authorisation`}
                          </button>
                        )}
                      </>
                    );
                  })()}
                />
              </div>
            )}

            {/* Action sheet modal — shared component (also used by the responder view). */}
            <ActionSheetModal
              open={actionSheetOpen}
              onClose={function () {
                setActionSheetOpen(null);
              }}
              req={req}
              nextStepsTemplates={nextStepsTemplates}
            />
          </div>
        );
      })()}
    </div>
  );
}
